import { useState, useCallback } from "react";
import { usePolling } from "../hooks/use-polling";
import { agentTasks, type Task, type TaskProcessStep } from "../lib/api";
import {
  CheckCircle2,
  Circle,
  XCircle,
  Loader2,
  SkipForward,
  RotateCw,
  AlertTriangle,
} from "lucide-react";

interface BuildProgressTrackerProps {
  taskId: string;
  onComplete?: () => void;
}

function StepIcon({ status }: { status: TaskProcessStep["status"] }) {
  switch (status) {
    case "done":
      return <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />;
    case "in_progress":
      return <Loader2 className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0" />;
    case "failed":
      return <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
    case "skipped":
      return <SkipForward className="w-4 h-4 text-gray-500 flex-shrink-0" />;
    default:
      return <Circle className="w-4 h-4 text-gray-600 flex-shrink-0" />;
  }
}

function statusColor(status: TaskProcessStep["status"]): string {
  switch (status) {
    case "done":
      return "text-green-400";
    case "in_progress":
      return "text-blue-400";
    case "failed":
      return "text-red-400";
    case "skipped":
      return "text-gray-500";
    default:
      return "text-gray-500";
  }
}

export default function BuildProgressTracker({ taskId, onComplete }: BuildProgressTrackerProps): JSX.Element | null {
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const [completed, setCompleted] = useState(false);

  const poll = useCallback(async () => {
    try {
      const res = await agentTasks.get(taskId);
      setTask(res.data);
      setLoading(false);
      if (res.data.status === "done" && !completed) {
        setCompleted(true);
        onComplete?.();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch task");
      setLoading(false);
    }
  }, [taskId, completed, onComplete]);

  usePolling(poll, 5_000);

  // Initial fetch
  if (loading && !task) {
    void poll();
  }

  async function handleResume() {
    setResuming(true);
    try {
      await agentTasks.resumeBuildWebsite(taskId);
      setError(null);
      void poll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Resume failed");
    } finally {
      setResuming(false);
    }
  }

  if (loading && !task) {
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" />
          <span className="text-sm text-zinc-400">Loading build progress...</span>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6">
        <p className="text-sm text-zinc-400">Task not found.</p>
      </div>
    );
  }

  const steps = task.process ?? [];
  const completedSteps = steps.filter((s) => s.status === "done").length;
  const totalSteps = steps.length;
  const progressPct = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  return (
    <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-100">{task.title}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {completedSteps}/{totalSteps} steps completed
          </p>
        </div>
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            task.status === "done"
              ? "bg-green-500/10 text-green-400"
              : task.status === "failed"
              ? "bg-red-500/10 text-red-400"
              : task.status === "in_progress"
              ? "bg-blue-500/10 text-blue-400"
              : "bg-zinc-700 text-zinc-400"
          }`}
        >
          {task.status.replace("_", " ")}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            task.status === "failed" ? "bg-red-500" : task.status === "done" ? "bg-green-500" : "bg-blue-500"
          }`}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Error alert */}
      {task.error && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-red-400">{task.error}</p>
          </div>
        </div>
      )}

      {/* Step list */}
      <div className="space-y-1">
        {steps.map((step, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
              step.status === "in_progress" ? "bg-zinc-700/50" : ""
            }`}
          >
            <StepIcon status={step.status} />
            <span className={`text-sm flex-1 ${statusColor(step.status)}`}>
              {step.name}
            </span>
            {step.completedAt && (
              <span className="text-xs text-zinc-600">
                {new Date(step.completedAt).toLocaleTimeString()}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Resume button */}
      {task.status === "failed" && (
        <button
          onClick={handleResume}
          disabled={resuming}
          className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <RotateCw className={`w-4 h-4 ${resuming ? "animate-spin" : ""}`} />
          {resuming ? "Resuming..." : "Resume Build"}
        </button>
      )}

      {error && !task.error && (
        <p className="text-xs text-red-400">{error}</p>
      )}
    </div>
  );
}
